import {defineStore} from "pinia";
import CheckpointSummary from "~/dto/checkpoint/Summary";
import type CheckpointSummaryData from "~~/api/response/data/checkpoint/SummaryData";
import type CheckpointDetailsResponse from "~~/api/response/CheckpointDetailsResponse";
import type CheckpointDetailsData from "~~/api/response/data/checkpoint/DetailsData";
import CheckpointDetails from "~/dto/checkpoint/Details";

export const useCheckpointDetailsStore = defineStore('checkpoint-details', () => {
  const { $apiFetch } = useNuxtApp();

  const summaryStore = useSummaryStore();
  const {summaryPending: summaryPending} = storeToRefs(summaryStore);

  const checkpointSummaryData = ref<CheckpointSummaryData | null>(null);
  const checkpointSummary = computed<CheckpointSummary | null>(() => {
    return checkpointSummaryData.value ? new CheckpointSummary(checkpointSummaryData.value) : null;
  });

  const checkpointDetailsData = ref<CheckpointDetailsData | null>(null);
  const checkpointDetails = computed<CheckpointDetails | null>(() => {
    return checkpointDetailsData.value ? new CheckpointDetails(checkpointDetailsData.value) : null;
  });
  const checkpointDetailsPending = ref<boolean>(false);

  const detailsFetchTimeout = 30;
  let detailsFetchInterval: NodeJS.Timeout | undefined;

  const checkpointDetailsItemsLimit = ref<number>(50);

  async function fetchSummaryData(code: string): Promise<CheckpointSummaryData | null> {
    await summaryStore.fetch();

    checkpointSummaryData.value = summaryStore.getCheckpointData(code);

    return checkpointSummaryData.value;
  }

  function updateSummaryData(data: CheckpointDetailsData) {
    summaryStore.updateFromDetailsData(data);
  }

  async function fetchDetailsData(code: string): Promise<CheckpointDetailsData | null> {
    checkpointDetailsPending.value = true;

    try {
      const {data} = await $apiFetch<CheckpointDetailsResponse>(
        `/checkpoints/${code}/details`,
        {query: {'items-limit': checkpointDetailsItemsLimit.value}}
      );
      checkpointDetailsData.value = data;

      if (data) {
        updateSummaryData(data);
      }

      return data;
    } catch (error) {
      captureException(error);

      return null;
    } finally {
      checkpointDetailsPending.value = false;
    }
  }

  function startDetailsPooling(code: string) {
    detailsFetchInterval = setInterval(
      async () => {
        await fetchSummaryData(code);
        await fetchDetailsData(code);
      },
      1000 * detailsFetchTimeout
    );
  }

  async function restartDetailsPooling(code: string, itemsLimit: number) {
    checkpointDetailsItemsLimit.value = itemsLimit;

    await fetchDetailsData(code)

    clearInterval(detailsFetchInterval);

    startDetailsPooling(code);
  }

  function clearData() {
    checkpointSummaryData.value = null;
    checkpointDetailsData.value = null;
    checkpointDetailsItemsLimit.value = 50;
    clearInterval(detailsFetchInterval);
  }

  return {
    fetchSummaryData,
    fetchDetailsData,
    startDetailsPooling,
    restartDetailsPooling,
    clearData,
    checkpointSummary,
    checkpointSummaryData,
    summaryPending,
    checkpointDetails,
    checkpointDetailsData,
    checkpointDetailsItemsLimit,
    checkpointDetailsPending,
  };
});
